import { gql } from 'graphql-request';
import { Moment } from 'moment';
import { GraphQlQuery } from 'interfaces/api';

export type UserRequest = {
  returnPartialFlights: boolean;
  r: string;
  adults: number;
  children: number;
  infants: number;
  from_location: string;
  to_location: string;
  from_radius?: number;
  to_radius?: number;
  from_airport?: string;
  to_airport?: string;
  departure_date: Moment;
  return_date?: Moment;
  extra_days?: number | string;
};
export type FlightSelection = {
  step: number;
  tripType: 'roundtrip' | 'one-way';
  filter: SearchFlightFilters;
};
export type SearchFlightFilters = {
  leaveOnlyUniqueFirstLegDepartureFlights?: boolean;
  leaveOnlyFirstLegDepartureFlightsWithFlightNumber?: string;
  leaveOnlyCombinationWithBookingId?: string;
  leaveOnlySegmentsWithStopId?: number;
  leaveOnlySegmentsWithAirlines?: Array<string>;
  leaveOnlySegmentsLessThanHourOutDepartureStart?: number;
  leaveOnlySegmentsLessThanHourOutDepartureEnd?: number;
  leaveOnlySegmentsLessThanHourOutArrivalStart?: number;
  leaveOnlySegmentsLessThanHourOutArrivalEnd?: number;
  leaveOnlySegmentsLessThanHourRetDepartureStart?: number;
  leaveOnlySegmentsLessThanHourRetDepartureEnd?: number;
  leaveOnlySegmentsLessThanHourRetArrivalStart?: number;
  leaveOnlySegmentsLessThanHourRetArrivalEnd?: number;
  leaveOnlySegementWithPriceLessThan?: number;
  leaveOnlySegementWithDurationLessThan?: number;
  selectedBookingId?: string;
};

export const getSearchFlightsQuery: (
  userRequest: UserRequest,
  filter: SearchFlightFilters,
) => Promise<GraphQlQuery> = async (userRequest, filter) => {
  const isReturnLeg =
    filter.leaveOnlyUniqueFirstLegDepartureFlights === false ||
    filter.leaveOnlyUniqueFirstLegDepartureFlights === undefined;
  const response = await fetch(
    isReturnLeg
      ? '/schemas/airfareSearchReturnLeg.txt'
      : '/schemas/airfareSearchFirstLeg.txt',
  );
  const text = await response.text();

  const query = gql`
    ${text}
  `;

  const personCollection = [];

  personCollection.push({
    passengerType: 'ADT',
    quantity: userRequest.adults,
  });

  if (userRequest.children) {
    personCollection.push({
      passengerType: 'CHD',
      quantity: userRequest.children,
    });
  }

  if (userRequest.infants) {
    personCollection.push({
      passengerType: 'INF',
      quantity: userRequest.infants,
    });
  }

  const variables = {
    returnPartialFlights: userRequest.returnPartialFlights,
    sessionId: userRequest.r,
    search: {
      fromLocation: userRequest.from_location,
      toLocation: userRequest?.to_location,
      fromRadius: userRequest?.from_radius,
      extraDays: userRequest?.extra_days,
      toRadius: userRequest.to_radius,
      departureDate:
        userRequest.departure_date &&
        userRequest.departure_date.format('YYYY-MM-DD[T]HH:mm:ss'),
      returnDate:
        userRequest.return_date &&
        userRequest.return_date.format('YYYY-MM-DD[T]HH:mm:ss'),
      persons: personCollection,
      fromAirport: userRequest.from_airport,
      toAirport: userRequest.to_airport,
    },
    filter: {
      leaveOnlyUniqueFirstLegDepartureFlights:
        filter.leaveOnlyUniqueFirstLegDepartureFlights,
      leaveOnlyFirstLegDepartureFlightsWithFlightNumber:
        filter.leaveOnlyFirstLegDepartureFlightsWithFlightNumber,
      leaveOnlyCombinationWithBookingId:
        filter.leaveOnlyCombinationWithBookingId,
      leaveOnlySegmentsWithStopId: filter.leaveOnlySegmentsWithStopId,
      leaveOnlySegmentsWithAirlines: filter.leaveOnlySegmentsWithAirlines,
      leaveOnlySegmentsLessThanHourOutDepartureStart:
        filter.leaveOnlySegmentsLessThanHourOutDepartureStart,
      leaveOnlySegmentsLessThanHourOutDepartureEnd:
        filter.leaveOnlySegmentsLessThanHourOutDepartureEnd,
      leaveOnlySegmentsLessThanHourOutArrivalStart:
        filter.leaveOnlySegmentsLessThanHourOutArrivalStart,
      leaveOnlySegmentsLessThanHourOutArrivalEnd:
        filter.leaveOnlySegmentsLessThanHourOutArrivalEnd,
      leaveOnlySegmentsLessThanHourRetDepartureStart:
        filter.leaveOnlySegmentsLessThanHourRetDepartureStart,
      leaveOnlySegmentsLessThanHourRetDepartureEnd:
        filter.leaveOnlySegmentsLessThanHourRetDepartureEnd,
      leaveOnlySegmentsLessThanHourRetArrivalStart:
        filter.leaveOnlySegmentsLessThanHourRetArrivalStart,
      leaveOnlySegmentsLessThanHourRetArrivalEnd:
        filter.leaveOnlySegmentsLessThanHourRetArrivalEnd,
      leaveOnlySegementWithPriceLessThan:
        filter.leaveOnlySegementWithPriceLessThan,
      leaveOnlySegementWithDurationLessThan:
        filter.leaveOnlySegementWithDurationLessThan,
      selectedBookingId: filter?.selectedBookingId || '',
    },
  };

  return { query, variables };
};
